/* eslint-disable no-irregular-whitespace */
import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import Helmet from 'react-helmet';
import { Link, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';
import { reduxForm, Field, Fields, formValueSelector, getFormValues } from 'redux-form/immutable';
import { bindActionCreators, compose } from 'redux';
import { Button, CircularProgress } from '@material-ui/core';
import { Alert, AlertTitle, Box, Typography } from '@mui/material';
import Divider from '@material-ui/core/Divider';

import { REGISTER_FORM_IDENTIFIER } from 'containers/RegisterPage/constants';
import { selectUser } from 'entities/user/selectors';
import { required, isEmail, isLocationObject, isUSLocationObject, isEin } from 'utils/validators';
import FormTextField from 'retired/FormTextField';
import FormPasswordField from 'retired/FormPasswordField';
import { FormNonprofitDbSelector } from 'retired/FormNonprofitDbSelector';
import SplashWrapper from 'retired/SplashWrapper';
import { useInjectReducer } from 'utils/injectReducer';
import { useInjectEpics } from 'utils/injectEpics';
import { purple, purpleish } from 'utils/color-styles';
import countries from 'utils/countries';
import TermsOfService from 'components/TermsOfService';
import createTextMask from 'components/MaskInput/createTextMask';
import config from 'config';
import { useQuery } from 'entities/useQuery';
import OrganizationApi from 'entities/organization/api';
import { useNonprofitQuery } from 'generated/graphql';
import { SelectedNonprofit } from 'retired/FormNonprofitDbSelector/SelectedNonprofit';
import { FormLocationObjectField } from 'retired/FormLocationObjectField/FormLocationObjectField';

import { useQueryParams } from '../../utils/useQueryParams';
import FormCheckbox from '../../retired/FormCheckbox/index';

import {
  selectSubmitDone,
  selectErrorCode,
  selectErrorMsg,
  selectSubmitting,
  selectDuplicateNonprofitId,
  selectDuplicateNonprofitName,
} from './selectors';
import * as Actions from './actions';
import epics from './epics';
import reducer from './reducer';
import { clearNonprofitIdParam, clearStepParam, setNonprofitIdParam } from './utils';
import { OrganizationNotFound } from './OrganizationNotFoundFlow';
import { PostSubmissionModal } from './PostSubmissionModal';

const requiredValidator = required();
const isEmailValidator = isEmail();
const isLocationObjectValidator = isLocationObject();
const isUSLocationObjectValidator = isUSLocationObject();
const isEinValidator = isEin();

const key = 'registerPage';

const einMask = createTextMask({
  pattern: '99-9999999',
  stripMask: true,
});

const styles = {
  flexContainer: {
    display: 'flex',
    justifyContent: 'center',
  },
};

const RegisterPage = ({
  match,
  actions,
  handleSubmit,
  submitting,
  submitDone,
  errorCode,
  duplicateNonprofitId,
  duplicateNonprofitName,
  invalid,
  nonprofit,
  user,
  formValues,
}) => {
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });

  const urlSearchParams = useQueryParams();
  const step = urlSearchParams.get('step');

  const [location, setLocation] = useState('US');
  const [createGovEntityMode, setCreateGovEntityMode] = useState(false);
  const [organizationNotFound, setOrganizationNotFound] = useState(Boolean(step));
  const [preselectedNonprofitId, setPreselectedNonprofitId] = useState(
    urlSearchParams.get('nonprofitId')
  );

  const clearPreselectedNonprofit = () => {
    setPreselectedNonprofitId(null);
    clearNonprofitIdParam();
  };

  const partnerToken = match?.params?.partnerToken || urlSearchParams.get('partnerToken');

  const { loading, data } = useNonprofitQuery({
    variables: { organizationId: preselectedNonprofitId },
    skip: !preselectedNonprofitId,
    // @NOTE-DP: if the data fails to load, just fallback to the standard form
    onError: clearPreselectedNonprofit,
  });

  const selectedNonprofitId = nonprofit?.nonprofitId || preselectedNonprofitId;
  const preselectedNonprofit = data?.nonprofit;

  const submit =
    (override = {}) =>
    (payload) =>
      actions.saveAction({
        ...payload.toJS(),
        ...override,
        partnerToken,
        // @NOTE-DP: if we have a preselected nonprofit, override they payload with it
        ...(preselectedNonprofitId
          ? {
              nonprofit: { nonprofitId: preselectedNonprofitId },
            }
          : {}),
      });

  // @NOTE-DP: there's no point really in error handling here. If something goes wrong, we'll treat the nonprofit as unclaimed, no big deal
  const { data: isClaimed } = useQuery(
    () => OrganizationApi.checkIfClaimed(selectedNonprofitId).toPromise(),
    false,
    selectedNonprofitId,
    !selectedNonprofitId
  );

  useEffect(() => {
    if (nonprofit?.nonprofitId) {
      setNonprofitIdParam(nonprofit?.nonprofitId);
    }
  }, [nonprofit?.nonprofitId]);

  const preselectedNonprofitPreview =
    preselectedNonprofitId &&
    (loading ? (
      <CircularProgress />
    ) : (
      preselectedNonprofit && (
        <SelectedNonprofit
          selectedNonprofit={preselectedNonprofit}
          allowDeselection
          isOutlined
          onClear={clearPreselectedNonprofit}
        />
      )
    ));

  if (organizationNotFound) {
    return (
      <OrganizationNotFound
        onClose={() => {
          clearStepParam();
          setOrganizationNotFound(false);
        }}
        onCreateGov={() => {
          clearStepParam();
          setOrganizationNotFound(false);
          setCreateGovEntityMode(true);
        }}
      />
    );
  }

  return (
    <>
      <SplashWrapper>
        <Helmet title="Claim your profile" />
        <Typography variant="h1" mb={5}>
          {createGovEntityMode
            ? 'Government entity profile'
            : isClaimed
            ? 'Request Access'
            : 'Claim your profile'}
        </Typography>
        <form onSubmit={handleSubmit(submit())}>
          <div>
            {preselectedNonprofitId ? (
              preselectedNonprofitPreview
            ) : createGovEntityMode ? (
              <>
                <FormTextField
                  value="United States"
                  label="Nonprofit location"
                  helperText="Where are you located?"
                  disabled
                  style={{ marginBottom: 16 }}
                />
                <Field
                  name="nonprofitName"
                  component={FormTextField}
                  fullWidth
                  autoFocus
                  inputProps={{ maxLength: 100, autoComplete: 'off' }}
                  label={createGovEntityMode ? `Government entity name` : `Organization name`}
                  helperText="Please input your officially registered name."
                  autoComplete="off"
                  validate={[requiredValidator]}
                  style={{ marginBottom: 16 }}
                />
                <Field
                  name="ein"
                  component={FormTextField}
                  fullWidth
                  label="EIN"
                  helperText="Employer Identification Number (EIN)"
                  style={{ marginBottom: 16 }}
                  validate={isEinValidator}
                  {...einMask}
                />
                <Field
                  name="locationObject"
                  component={FormLocationObjectField}
                  fullWidth
                  label="Location"
                  helperText="Please enter a full organization address"
                  validate={[
                    requiredValidator,
                    isLocationObjectValidator,
                    isUSLocationObjectValidator,
                  ]}
                />
              </>
            ) : (
              <Field
                name="nonprofit"
                component={FormNonprofitDbSelector}
                fullWidth
                autoFocus
                limit={1}
                required
                validate={[requiredValidator]}
                initialLocation={countries.US.alpha2}
                variant="standard"
                inputComponent={FormTextField}
                style={{ marginBottom: 8 }}
                locationSelectorHelperText="Where are you located?"
                nonprofitSelectorLabel="Organization name"
                nonprofitSelectorHelperText="Who do you represent? Try searching for your organization's name or Employer Identification Number (EIN)"
                nonprofitSelectorNoOptions={null}
                onLocationSelect={setLocation}
                extraContent={
                  <Box
                    color={purple}
                    onMouseDown={() => {
                      if (location === 'US') {
                        setOrganizationNotFound(true);
                      } else {
                        window.open(
                          `mailto:${config.nonprofitSuccessTeamEmail}?subject=I cannot find my organization in the search`
                        );
                      }
                    }}
                    p={2}
                    bgcolor={purpleish}
                    style={{
                      cursor: 'pointer',
                      textDecoration: 'underline',
                    }}
                  >
                    Can’t find your organization?
                  </Box>
                }
              />
            )}

            {isClaimed && (
              <Alert severity="warning" style={{ marginBottom: 12 }}>
                <AlertTitle>This nonprofit is already claimed.</AlertTitle>
                If you need access to this particular organization and cannot locate its
                administrator, please complete the form to request access.
              </Alert>
            )}
            <Field
              name="fullName.first"
              component={FormTextField}
              fullWidth
              label="First Name"
              validate={[requiredValidator]}
              required
              style={{ marginBottom: 0 }}
            />
            <Field
              name="fullName.last"
              component={FormTextField}
              fullWidth
              label="Last Name"
              validate={[requiredValidator]}
              required
              style={{ marginBottom: 0 }}
            />
            <Field
              name="email"
              component={FormTextField}
              fullWidth
              label="Email Address"
              helperText="What email can we use to reach you? This is how you will login."
              validate={[isEmailValidator, requiredValidator]}
              required
              style={{ marginBottom: 0 }}
            />
            <Fields
              names={['password1', 'password2']}
              component={FormPasswordField}
              userInputs={{ currentUser: user }}
              errorText="Password fields must match."
              firstFieldProps={{
                name: 'password1',
                label: 'Password',
                style: { marginBottom: 0 },
              }}
              secondFieldProps={{
                name: 'password2',
                label: 'Confirm Password',
                required: true,
                style: { marginBottom: 36 },
              }}
            />
            <div style={{ marginBottom: 12 }}>
              <Field
                name="attestedAuthorizationForNonprofit"
                component={FormCheckbox}
                validate={[requiredValidator]}
                label="I confirm that I am authorized to register my organization with Deed."
              />
            </div>
            <Button
              type="submit"
              color="primary"
              variant="contained"
              disabled={submitting || submitDone || invalid}
              style={{ width: '100%' }}
            >
              {createGovEntityMode
                ? 'Create a government entity'
                : isClaimed
                ? 'Request Access'
                : 'Claim your profile'}
            </Button>
          </div>

          <div
            style={{
              ...styles.flexContainer,
              marginTop: '1em',
              textAlign: 'center',
            }}
          >
            <TermsOfService />
          </div>

          <Divider style={{ margin: '1em 0px .5em 0px' }} />

          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 20 }}>
            Already have an account?&nbsp;<Link to="/login">Login here!</Link>
          </div>
        </form>
      </SplashWrapper>
      <PostSubmissionModal
        submitDone={submitDone}
        errorCode={errorCode}
        reset={() => {
          setCreateGovEntityMode(false);
          actions.resetDialogAction();
        }}
        isClaimed={isClaimed}
        duplicateNonprofitId={duplicateNonprofitId}
        duplicateNonprofitName={duplicateNonprofitName}
        confirmDuplicateEin={() => {
          handleSubmit(submit({ confirmDuplicateEin: true }))(formValues);
        }}
        formValues={formValues?.toJS() || {}}
        selectNonprofit={(nonprofitId) => {
          setNonprofitIdParam(nonprofitId);
          setPreselectedNonprofitId(nonprofitId);
        }}
      />
    </>
  );
};

RegisterPage.propTypes = {
  user: PropTypes.object,
  invalid: PropTypes.bool,
  match: PropTypes.object,
  actions: PropTypes.object,
  handleSubmit: PropTypes.func,
  submitting: PropTypes.bool,
  submitDone: PropTypes.bool,
  errorCode: PropTypes.string,
  duplicateNonprofitId: PropTypes.string,
  duplicateNonprofitName: PropTypes.string,
  nonprofit: PropTypes.object,
  formValues: PropTypes.object,
};

const getFormValuesSelector = getFormValues(REGISTER_FORM_IDENTIFIER);
const selector = formValueSelector(REGISTER_FORM_IDENTIFIER);
const withConnect = connect(
  (state) => ({
    user: selectUser(state),
    submitting: selectSubmitting(state),
    submitDone: selectSubmitDone(state),
    errorCode: selectErrorCode(state),
    errorMsg: selectErrorMsg(state),
    duplicateNonprofitId: selectDuplicateNonprofitId(state),
    duplicateNonprofitName: selectDuplicateNonprofitName(state),
    nonprofit: selector(state, 'nonprofit'),
    formValues: getFormValuesSelector(state),
  }),
  (dispatch) => ({ actions: bindActionCreators(Actions, dispatch) })
);

const withForm = reduxForm({
  form: REGISTER_FORM_IDENTIFIER,
  destroyOnUnmount: false,
  enableReinitialize: true,
  keepDirtyOnReinitialize: true,
});

export default compose(withRouter, withConnect, withForm)(RegisterPage);
